<template>
  <div class="domains-register">
    <div class="domains-register__header">
      <div>
        <h1>Domain inventory</h1>
        <p>Every domain Apexl owns, reconciled from three sources into one register — in use or not, visible either way. A source going quiet flags rows; it never deletes them.</p>
      </div>
    </div>

    <div v-if="loadError" class="domains-register__error">{{ loadError }}</div>
    <div v-if="syncMessage" class="domains-register__notice">{{ syncMessage }}</div>
    <div v-if="bulkMessage" class="domains-register__notice">{{ bulkMessage }}</div>

    <!-- ═══════════ sync sources strip ═══════════ -->
    <div class="domains-register__sync-strip">
      <div class="domains-register__sync-card">
        <div class="domains-register__sync-top">
          <div class="domains-register__sync-icon"><SourceGlyph via="enom" /></div>
          <div>
            <div class="domains-register__sync-name">Enom</div>
            <div class="domains-register__sync-role">registrar — the backbone list</div>
          </div>
        </div>
        <div class="domains-register__sync-status">
          <div class="domains-register__sync-freshness">
            listDomains()
            <span class="domains-register__sync-ts">{{ enomFreshness.text }}</span>
          </div>
          <span class="domains-register__fresh-pill" :class="`domains-register__fresh-pill--${enomFreshness.tone}`">{{ enomFreshness.pill }}</span>
        </div>
        <button type="button" class="domains-register__sync-btn" :disabled="syncing" @click="triggerSync">
          {{ syncing ? 'Syncing…' : 'Sync now' }}
        </button>
      </div>

      <div class="domains-register__sync-card">
        <div class="domains-register__sync-top">
          <div class="domains-register__sync-icon"><SourceGlyph via="cloudflare" /></div>
          <div>
            <div class="domains-register__sync-name">Cloudflare</div>
            <div class="domains-register__sync-role">zones — DNS we run, registrar elsewhere or not</div>
          </div>
        </div>
        <div class="domains-register__sync-status">
          <div class="domains-register__sync-freshness">
            listZones()
            <span class="domains-register__sync-ts">{{ cloudflareFreshness.text }}</span>
          </div>
          <span class="domains-register__fresh-pill" :class="`domains-register__fresh-pill--${cloudflareFreshness.tone}`">{{ cloudflareFreshness.pill }}</span>
        </div>
        <button type="button" class="domains-register__sync-btn" :disabled="syncing" @click="triggerSync">
          {{ syncing ? 'Syncing…' : 'Sync now' }}
        </button>
      </div>

      <div class="domains-register__sync-card">
        <div class="domains-register__sync-top">
          <div class="domains-register__sync-icon"><SourceGlyph via="manual" /></div>
          <div>
            <div class="domains-register__sync-name">Manual</div>
            <div class="domains-register__sync-role">CSV import + hand-entered rows</div>
          </div>
        </div>
        <div class="domains-register__sync-status">
          <div class="domains-register__sync-freshness">
            {{ manualCount }} {{ manualCount === 1 ? 'domain' : 'domains' }}, no auto-sweep
            <span class="domains-register__sync-ts">{{ manualLastAddition ? `last addition ${manualLastAddition}` : 'none yet' }}</span>
          </div>
          <span class="domains-register__fresh-pill domains-register__fresh-pill--manual">No sweep</span>
        </div>
        <button type="button" class="domains-register__sync-btn domains-register__sync-btn--quiet" @click="scrollToImport">
          + Add or import below
        </button>
      </div>
    </div>

    <!-- ═══════════ import / add ═══════════ -->
    <div ref="importCard" class="domains-register__import-grid">
      <div class="domains-register__import-col">
        <h5>CSV import</h5>
        <p class="domains-register__ic-note">For registrars we don't integrate with. Required, not optional — Enom + Cloudflare alone would quietly under-represent the real estate.</p>
        <div
          class="domains-register__dropzone"
          :class="{ 'domains-register__dropzone--over': dragOver }"
          @click="$refs.csvInput.click()"
          @dragover.prevent="dragOver = true"
          @dragleave.prevent="dragOver = false"
          @drop.prevent="onCsvDrop"
        >
          <div><strong>Drop a CSV</strong> or click to browse</div>
          <span class="domains-register__dropzone-fmt">columns: domain, registrar, expiry, auto_renew, notes</span>
          <input ref="csvInput" type="file" accept=".csv,text/csv" style="display:none" @change="onCsvPicked" />
        </div>
        <div v-if="importResult" class="domains-register__import-result" :class="{ 'domains-register__import-result--error': importResult.failed }">
          {{ importResult.text }}
        </div>
      </div>
      <div class="domains-register__import-col">
        <h5>Add an owned domain</h5>
        <p class="domains-register__ic-note">One-off entry for a single domain — same destination row as the CSV path, <code>discovered_via: manual</code>.</p>
        <form class="domains-register__form-grid" @submit.prevent="addDomain">
          <div class="domains-register__form-field domains-register__form-field--full">
            <label>Domain name</label>
            <input v-model="addForm.domain" type="text" placeholder="example-unused.com" />
          </div>
          <div class="domains-register__form-field">
            <label>Registrar</label>
            <input v-model="addForm.registrar" type="text" placeholder="e.g. Namecheap" />
          </div>
          <div class="domains-register__form-field">
            <label>Expiry date</label>
            <input v-model="addForm.expiry" type="text" placeholder="YYYY-MM-DD (optional)" />
          </div>
          <div class="domains-register__form-field domains-register__form-field--full">
            <label>Notes — why was this bought?</label>
            <textarea v-model="addForm.notes" placeholder="e.g. reserved for a rebrand that didn't happen"></textarea>
          </div>
          <div class="domains-register__form-actions">
            <span v-if="addError" class="domains-register__form-error">{{ addError }}</span>
            <button type="submit" class="domains-register__btn-primary" :disabled="!addForm.domain || addBusy">
              {{ addBusy ? 'Adding…' : 'Add domain' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ═══════════ summary + quick alerts ═══════════ -->
    <div class="domains-register__summary">
      <div class="domains-register__sum-card domains-register__sum-card--total"><div class="domains-register__sval">{{ domains.length || '—' }}</div><div class="domains-register__slabel">Total domains</div></div>
      <div class="domains-register__sum-card domains-register__sum-card--inuse"><div class="domains-register__sval">{{ stateCounts.in_use || 0 }}</div><div class="domains-register__slabel">In use</div></div>
      <div class="domains-register__sum-card domains-register__sum-card--parked"><div class="domains-register__sval">{{ stateCounts.parked || 0 }}</div><div class="domains-register__slabel">Parked</div></div>
      <div class="domains-register__sum-card domains-register__sum-card--abandoned"><div class="domains-register__sval">{{ stateCounts.abandoned || 0 }}</div><div class="domains-register__slabel">Abandoned</div></div>
      <div class="domains-register__sum-card domains-register__sum-card--unknown"><div class="domains-register__sval">{{ stateCounts.unknown || 0 }}</div><div class="domains-register__slabel">Unknown</div></div>
    </div>

    <div v-if="expiringSoonCount || flaggedCount" class="domains-register__quick-alerts">
      <span
        v-if="expiringSoonCount"
        class="domains-register__quick-alert domains-register__quick-alert--warn"
        :class="{ 'domains-register__quick-alert--on': expiringOnly }"
        @click="toggleExpiringOnly"
      >⚠ {{ expiringSoonCount }} expiring within 60 days</span>
      <span
        v-if="flaggedCount"
        class="domains-register__quick-alert domains-register__quick-alert--danger"
        :class="{ 'domains-register__quick-alert--on': filters.review.has('flagged') }"
        @click="toggleFacet('review', 'flagged')"
      >⚑ {{ flaggedCount }} flagged for review</span>
    </div>

    <!-- ═══════════ register: rail + table ═══════════ -->
    <div class="domains-register__layout">
      <div class="domains-register__rail">
        <div class="domains-register__rail-head">
          <h4>Filters</h4>
          <span v-if="hasActiveFilters" class="domains-register__clear" @click="clearFilters">Clear</span>
        </div>

        <div v-for="group in facetGroups" :key="group.key" class="domains-register__facet-group">
          <div class="domains-register__facet-title">{{ group.title }}</div>
          <div
            v-for="term in group.terms"
            :key="term.key"
            class="domains-register__facet-row"
            :class="{ 'domains-register__facet-row--active': filters[group.key].has(term.key) }"
            @click="toggleFacet(group.key, term.key)"
          >
            <span class="domains-register__facet-box"></span>
            <span class="domains-register__facet-label">{{ term.label }}</span>
            <span class="domains-register__facet-count">{{ term.count }}</span>
          </div>
        </div>
      </div>

      <div class="domains-register__grid">
        <div v-if="selection.size" class="domains-register__bulk-bar">
          <span class="domains-register__bcount">{{ selection.size }} selected</span>
          <button type="button" class="domains-register__bulk-btn" :disabled="bulkBusy" @click="runBulk('mark_parked')">Mark parked</button>
          <button type="button" class="domains-register__bulk-btn" :disabled="bulkBusy" @click="runBulk('mark_in_use')">Mark in use</button>
          <button type="button" class="domains-register__bulk-btn" :disabled="bulkBusy" @click="runBulk('disable_auto_renew')">Disable auto-renew</button>
          <button type="button" class="domains-register__bulk-btn domains-register__bulk-btn--ghost" @click="selection.clear()">Clear selection</button>
        </div>

        <div class="domains-register__toolbar">
          <div class="domains-register__search-box">
            <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="8" cy="8" r="5"/><path d="M12 12l4 4"/></svg>
            <input v-model="search" type="text" placeholder="Search domain name…" />
          </div>
          <div class="domains-register__toolbar-right">
            <button type="button" class="domains-register__sort-btn" @click="cycleSort">Sort: {{ sortModes[sortIndex].label }}</button>
          </div>
        </div>

        <table>
          <thead>
            <tr><th></th><th>Domain</th><th>Registrar</th><th>Expiry</th><th>Auto-renew</th><th>Usage state</th><th>Linked site</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="domains-register__loading">Loading the inventory…</td>
            </tr>
            <tr v-else-if="!filteredDomains.length">
              <td colspan="8" class="domains-register__loading">No domains match the current filters.</td>
            </tr>
            <DomainRow
              v-for="domain in filteredDomains"
              :key="domain.id"
              :domain="domain"
              density="table"
              :checked="selection.has(domain.id)"
              @click="openDrawer"
              @check="toggleSelect"
              @resolve-review="resolveReview"
              @recheck="triggerSync"
            />
          </tbody>
        </table>
      </div>
    </div>

    <DomainDetailDrawer
      v-if="drawerDomain"
      :domain="drawerDomain"
      :api-base="apiBase"
      @close="drawerDomain = null"
      @updated="onDomainUpdated"
      @recheck="triggerSync"
    />

    <DomainPickerModal
      v-if="pickerOpen"
      :api-base="apiBase"
      context-label="Preview — the provisioning run surface mounts this at step 3a (M4)"
      @close="closePicker"
      @attach="closePicker"
      @register-new="closePicker"
    />
  </div>
</template>

<script setup>
// The `/domains` register — Plan 005 §4.6, built exactly to the approved
// `design/surfaces/domains-register.html` mockup. Mounted by
// `app\module\domains\Controller\DomainsDisplayController::index()`; owns
// its own data-fetching/filters/drawer state, mirroring
// `ServersFleetBoard.vue`'s whole-owned-page shape. All rows render through
// the ONE canonical `DomainRow` component (density `table` here; the detail
// drawer uses `header`, the step-3a picker uses `picker`).
//
// The step-3a picker is a reusable component whose real consumer is M4's
// provisioning-run surface; until that lands, `?picker=preview` on this
// route opens it for review so the built surface never goes stale unseen.
import { computed, defineComponent, h, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import useApi from '@apexl/icarus-core/src/services/api';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';
import DomainRow from '@root/components/Domains/DomainRow.vue';
import DomainDetailDrawer from '@root/components/Domains/DomainDetailDrawer.vue';
import DomainPickerModal from '@root/components/Domains/DomainPickerModal.vue';
import {
  daysUntil,
  discoveredViaLabel,
  formatShortDate,
  relativeTime,
  stateLabel,
} from '@root/services/domainFormat';

const SourceGlyph = defineComponent({
  props: { via: { type: String, required: true } },
  setup(props) {
    const paths = {
      enom: [
        ['path', { d: 'M2.5 3h4.9l6.6 6.6-4.9 4.9L2.5 7.9Z' }],
        ['circle', { cx: 6, cy: 5.6, r: 0.7, fill: 'currentColor', stroke: 'none' }],
      ],
      cloudflare: [
        ['path', { d: 'M9 2.5 14.5 4.5v4.5c0 3.4-2.3 5.8-5.5 6.5-3.2-.7-5.5-3.1-5.5-6.5V4.5Z' }],
      ],
      manual: [
        ['path', { d: 'M3 15l.5-2.6L11.5 4.4a1.1 1.1 0 0 1 1.6 0l.5.5a1.1 1.1 0 0 1 0 1.6L5.6 14.5 3 15Z' }],
      ],
    };
    return () => h(
      'svg',
      { viewBox: '0 0 18 18', fill: 'none', stroke: 'currentColor', 'stroke-width': '1.6', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' },
      (paths[props.via] || paths.manual).map(([tag, attrs]) => h(tag, attrs)),
    );
  },
});

const props = defineProps({
  ...commonProps,
  apiBase: {
    type: String,
    default: '/api/v1/domains',
  },
});

const api = useApi();
const route = useRoute();
const router = useRouter();

const domains = ref([]);
const loading = ref(false);
const loadError = ref(null);

const search = ref('');
const filters = reactive({
  state: new Set(),
  via: new Set(),
  autorenew: new Set(),
  review: new Set(),
});
const expiringOnly = ref(false);

const selection = reactive(new Set());
const bulkBusy = ref(false);
const bulkMessage = ref(null);

const drawerDomain = ref(null);

const syncing = ref(false);
const syncMessage = ref(null);
const lastSyncResult = ref(null);

const dragOver = ref(false);
const importResult = ref(null);
const importCard = ref(null);

const addForm = reactive({ domain: '', registrar: '', expiry: '', notes: '' });
const addBusy = ref(false);
const addError = ref(null);

// ── data ────────────────────────────────────────────────────────────────
async function loadDomains() {
  loading.value = true;
  loadError.value = null;
  try {
    const data = await api.get(props.apiBase);
    domains.value = data.domains || [];
  } catch (err) {
    loadError.value = 'Could not load the domain inventory — check your connection and try again.';
  } finally {
    loading.value = false;
  }
}

// ── summary / quick alerts ──────────────────────────────────────────────
const stateCounts = computed(() => {
  const counts = {};
  for (const domain of domains.value) {
    counts[domain.usage_state] = (counts[domain.usage_state] || 0) + 1;
  }
  return counts;
});

const expiringSoonCount = computed(() => domains.value.filter((d) => {
  const days = daysUntil(d.expires_at);
  return days !== null && days <= 60;
}).length);

const flaggedCount = computed(() => domains.value.filter((d) => d.review_flagged_at).length);

function toggleExpiringOnly() {
  expiringOnly.value = !expiringOnly.value;
}

// ── sync strip ──────────────────────────────────────────────────────────
// Manual rows get `last_synced_at` stamped at creation (the import service's
// bookkeeping), but they are never touched by the sweep — exclude them so a
// fresh manual add can't make the source cards claim a sweep just ran.
const lastSweepAt = computed(() => domains.value
  .filter((d) => d.discovered_via !== 'manual')
  .reduce((max, d) => (d.last_synced_at && d.last_synced_at > max ? d.last_synced_at : max), 0));

function sourceFreshness(sourceKey) {
  const result = lastSyncResult.value?.[sourceKey];
  if (result && !result.ran) {
    return { text: result.skipped_reason || 'skipped', pill: 'Skipped', tone: 'stale' };
  }
  if (result && result.ran) {
    return {
      text: `just now · ${result.created} new, ${result.updated} updated`,
      pill: 'Fresh',
      tone: 'ok',
    };
  }
  if (!lastSweepAt.value) {
    return { text: 'no sweep recorded yet', pill: 'Never', tone: 'stale' };
  }
  const ageSeconds = Date.now() / 1000 - lastSweepAt.value;
  return {
    text: `last sweep ${relativeTime(lastSweepAt.value)}`,
    pill: ageSeconds < 2 * 3600 ? 'Fresh' : 'Stale',
    tone: ageSeconds < 2 * 3600 ? 'ok' : 'stale',
  };
}

const enomFreshness = computed(() => sourceFreshness('enom'));
const cloudflareFreshness = computed(() => sourceFreshness('cloudflare'));

const manualRows = computed(() => domains.value.filter((d) => d.discovered_via === 'manual'));
const manualCount = computed(() => manualRows.value.length);
const manualLastAddition = computed(() => {
  const latest = manualRows.value.reduce((max, d) => (d.created && d.created > max ? d.created : max), 0);
  return latest ? formatShortDate(latest) : null;
});

async function triggerSync() {
  syncing.value = true;
  syncMessage.value = null;
  try {
    const data = await api.post(`${props.apiBase}/sync`, {});
    lastSyncResult.value = data.result || null;
    const flagged = data.result?.flagged_for_review || 0;
    syncMessage.value = flagged
      ? `Sync complete — ${flagged} domain${flagged === 1 ? '' : 's'} newly flagged for review.`
      : null;
    await loadDomains();
    if (drawerDomain.value) {
      drawerDomain.value = domains.value.find((d) => d.id === drawerDomain.value.id) || null;
    }
  } catch (err) {
    syncMessage.value = err?.response?.status === 409
      ? 'A sync is already in progress — try again shortly.'
      : (err?.response?.data?.error || 'Sync failed — check the integration credentials.');
  } finally {
    syncing.value = false;
  }
}

// ── import / add ────────────────────────────────────────────────────────
function scrollToImport() {
  importCard.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function onCsvPicked(event) {
  const file = event.target.files?.[0];
  if (file) importCsvFile(file);
  event.target.value = '';
}

function onCsvDrop(event) {
  dragOver.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) importCsvFile(file);
}

async function importCsvFile(file) {
  importResult.value = null;
  try {
    const csv = await file.text();
    const data = await api.post(`${props.apiBase}/import`, { csv });
    const errorCount = (data.errors || []).length;
    importResult.value = {
      failed: false,
      text: `Imported ${data.imported}, updated ${data.updated}${errorCount ? ` — ${errorCount} line${errorCount === 1 ? '' : 's'} had problems: ${data.errors.slice(0, 3).join(' · ')}` : '.'}`,
    };
    await loadDomains();
  } catch (err) {
    importResult.value = {
      failed: true,
      text: err?.response?.data?.error || 'Import failed — check the CSV format.',
    };
  }
}

async function addDomain() {
  addBusy.value = true;
  addError.value = null;
  try {
    await api.post(props.apiBase, {
      domain: addForm.domain.trim(),
      registrar: addForm.registrar.trim() || undefined,
      expiry: addForm.expiry.trim() || undefined,
      notes: addForm.notes.trim() || undefined,
    });
    Object.assign(addForm, { domain: '', registrar: '', expiry: '', notes: '' });
    await loadDomains();
  } catch (err) {
    addError.value = err?.response?.data?.error || 'Could not add that domain.';
  } finally {
    addBusy.value = false;
  }
}

// ── facets / filtering / sorting ────────────────────────────────────────
const facetGroups = computed(() => {
  const viaCounts = {};
  const arCounts = { on: 0, off: 0 };
  let manualOverride = 0;
  for (const d of domains.value) {
    if (d.discovered_via) viaCounts[d.discovered_via] = (viaCounts[d.discovered_via] || 0) + 1;
    arCounts[Number(d.auto_renew) ? 'on' : 'off'] += 1;
    if (d.usage_state_source === 'manual') manualOverride += 1;
  }

  return [
    {
      key: 'state',
      title: 'Usage state',
      terms: ['in_use', 'parked', 'abandoned', 'unknown'].map((s) => ({ key: s, label: stateLabel(s), count: stateCounts.value[s] || 0 })),
    },
    {
      key: 'via',
      title: 'Discovered via',
      terms: ['enom', 'cloudflare', 'manual', 'pipeline']
        .filter((v) => viaCounts[v])
        .map((v) => ({ key: v, label: discoveredViaLabel(v), count: viaCounts[v] })),
    },
    {
      key: 'autorenew',
      title: 'Auto-renew',
      terms: [
        { key: 'on', label: 'On', count: arCounts.on },
        { key: 'off', label: 'Off', count: arCounts.off },
      ],
    },
    {
      key: 'review',
      title: 'Review',
      terms: [
        { key: 'flagged', label: 'Flagged', count: flaggedCount.value },
        { key: 'manual', label: 'Manual override', count: manualOverride },
      ],
    },
  ];
});

function toggleFacet(groupKey, termKey) {
  const set = filters[groupKey];
  if (set.has(termKey)) {
    set.delete(termKey);
  } else {
    set.add(termKey);
  }
}

const hasActiveFilters = computed(() => (
  filters.state.size > 0 || filters.via.size > 0 || filters.autorenew.size > 0
  || filters.review.size > 0 || expiringOnly.value || search.value !== ''
));

function clearFilters() {
  filters.state.clear();
  filters.via.clear();
  filters.autorenew.clear();
  filters.review.clear();
  expiringOnly.value = false;
  search.value = '';
}

const sortModes = [
  { key: 'expires_at', dir: 1, label: 'Expiry ↑', missingLast: true },
  { key: 'expires_at', dir: -1, label: 'Expiry ↓', missingLast: true },
  { key: 'domain', dir: 1, label: 'Domain A→Z' },
  { key: 'domain', dir: -1, label: 'Domain Z→A' },
  { key: 'last_synced_at', dir: -1, label: 'Recently synced', missingLast: true },
];
const sortIndex = ref(0);

function cycleSort() {
  sortIndex.value = (sortIndex.value + 1) % sortModes.length;
}

const filteredDomains = computed(() => {
  const mode = sortModes[sortIndex.value];

  return domains.value
    .filter((d) => {
      if (search.value && !d.domain.toLowerCase().includes(search.value.toLowerCase())) return false;
      if (filters.state.size && !filters.state.has(d.usage_state)) return false;
      if (filters.via.size && !filters.via.has(d.discovered_via)) return false;
      if (filters.autorenew.size && !filters.autorenew.has(Number(d.auto_renew) ? 'on' : 'off')) return false;
      if (filters.review.size) {
        const matches = (filters.review.has('flagged') && d.review_flagged_at)
          || (filters.review.has('manual') && d.usage_state_source === 'manual');
        if (!matches) return false;
      }
      if (expiringOnly.value) {
        const days = daysUntil(d.expires_at);
        if (days === null || days > 60) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const av = a[mode.key];
      const bv = b[mode.key];
      if (mode.missingLast) {
        if (!av && !bv) return 0;
        if (!av) return 1;
        if (!bv) return -1;
      }
      if (typeof av === 'string') return av.localeCompare(bv) * mode.dir;
      return ((av || 0) - (bv || 0)) * mode.dir;
    });
});

// ── selection / bulk ────────────────────────────────────────────────────
function toggleSelect(domain) {
  if (selection.has(domain.id)) {
    selection.delete(domain.id);
  } else {
    selection.add(domain.id);
  }
}

async function runBulk(action) {
  bulkBusy.value = true;
  bulkMessage.value = null;
  try {
    const data = await api.post(`${props.apiBase}/bulk`, { ids: [...selection], action });
    if (action === 'disable_auto_renew') {
      // io-domains 1.3.0: the backend now writes the registrar-side flag when
      // it can, so the two outcomes are genuinely different — say which is which.
      const done = data.disabled_at_registrar || 0;
      const flagged = data.flagged_for_review || 0;
      const parts = [];
      if (done) parts.push(`${done} disabled at the registrar`);
      if (flagged) parts.push(`${flagged} disabled locally and flagged for review — finish those in the registrar panel`);
      bulkMessage.value = parts.length ? `Auto-renew: ${parts.join(' · ')}.` : null;
    }
    selection.clear();
    await loadDomains();
  } catch (err) {
    loadError.value = err?.response?.data?.error || 'Bulk action failed.';
  } finally {
    bulkBusy.value = false;
  }
}

// ── drawer / review ─────────────────────────────────────────────────────
function openDrawer(domain) {
  drawerDomain.value = domain;
}

function onDomainUpdated(domain) {
  const idx = domains.value.findIndex((d) => d.id === domain.id);
  if (idx !== -1) domains.value.splice(idx, 1, domain);
  drawerDomain.value = domain;
}

async function resolveReview(domain) {
  try {
    const data = await api.post(`${props.apiBase}/${domain.id}/review/resolve`, {});
    onDomainUpdated(data.domain);
    drawerDomain.value = null;
  } catch (err) {
    loadError.value = err?.response?.data?.error || 'Could not resolve the review flag.';
  }
}

// ── step-3a picker preview ──────────────────────────────────────────────
const pickerOpen = computed(() => route.query.picker !== undefined);

function closePicker() {
  const query = { ...route.query };
  delete query.picker;
  router.replace({ query });
}

onMounted(loadDomains);
</script>

<style lang="scss">
.domains-register {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 18px;
    flex-wrap: wrap;

    h1 {
      font-size: 20px;
      font-weight: 600;
      margin: 0 0 4px;
    }

    p {
      font-size: 13px;
      color: var(--theme-text-secondary);
      margin: 0;
      max-width: 80ch;
    }
  }

  &__error,
  &__notice {
    border-radius: var(--theme-radius, 6px);
    padding: 10px 14px;
    font-size: 12.5px;
    margin-bottom: 14px;
  }

  &__error {
    background: #f8dede;
    color: #a53232;
  }

  &__notice {
    background: var(--theme-accent-light);
    color: var(--theme-accent-dark);
  }

  // ── sync strip ─────────────────────────────────────────────────────────
  &__sync-strip {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 12px;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  &__sync-card {
    padding: 16px 18px;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
  }

  &__sync-top {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }

  &__sync-icon {
    width: 32px;
    height: 32px;
    border-radius: var(--theme-radius, 6px);
    background: var(--theme-accent-light);
    color: var(--theme-accent-dark);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  &__sync-name {
    font-size: 13px;
    font-weight: 600;
  }

  &__sync-role {
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    margin-top: 1px;
  }

  &__sync-status {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
  }

  &__sync-freshness {
    font-size: 12px;
    color: var(--theme-text-secondary);
  }

  &__sync-ts {
    display: block;
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    margin-top: 3px;
    overflow-wrap: anywhere;
  }

  &__fresh-pill {
    font-size: 10px;
    font-weight: 700;
    padding: 2.5px 8px;
    border-radius: 3px;
    white-space: nowrap;
    flex-shrink: 0;

    &--ok {
      background: #e1f3ea;
      color: #1c7a4d;
    }

    &--stale {
      background: #fbeeda;
      color: #9c6a10;
    }

    &--manual {
      background: var(--theme-border-soft);
      color: var(--theme-text-secondary);
    }
  }

  &__sync-btn {
    margin-top: 13px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 11.5px;
    font-weight: 600;
    padding: 7px;
    border-radius: var(--theme-radius-sm, 3px);
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);
    color: var(--theme-text-primary);
    cursor: pointer;

    &:disabled {
      opacity: .6;
      cursor: default;
    }

    &--quiet {
      border-color: transparent;
      color: var(--theme-accent-dark);
      background: transparent;
    }
  }

  // ── import / add ───────────────────────────────────────────────────────
  &__import-grid {
    display: grid;
    grid-template-columns: 1fr 1.4fr;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    margin-bottom: 16px;
    overflow: hidden;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  &__import-col {
    padding: 18px 20px;

    & + & {
      border-left: 1px solid var(--theme-border-soft);

      @media (max-width: 900px) {
        border-left: none;
        border-top: 1px solid var(--theme-border-soft);
      }
    }

    h5 {
      margin: 0 0 3px;
      font-size: 12.5px;
      font-weight: 600;
    }

    code {
      font-family: var(--theme-font-mono);
      background: var(--theme-border-soft);
      padding: 1px 4px;
      border-radius: 3px;
      font-size: 10.5px;
    }
  }

  &__ic-note {
    font-size: 11px;
    color: var(--theme-text-tertiary);
    margin: 0 0 12px;
    line-height: 1.5;
  }

  &__dropzone {
    border: 1.5px dashed var(--theme-border-strong);
    border-radius: var(--theme-radius, 6px);
    padding: 24px 16px;
    text-align: center;
    color: var(--theme-text-tertiary);
    font-size: 12px;
    cursor: pointer;

    strong {
      color: var(--theme-accent-dark);
      font-weight: 600;
    }

    &--over {
      border-color: var(--theme-accent-primary);
      background: var(--theme-accent-light);
    }
  }

  &__dropzone-fmt {
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    margin-top: 6px;
    display: block;
  }

  &__import-result {
    margin-top: 10px;
    font-size: 11.5px;
    color: var(--theme-text-secondary);
    line-height: 1.5;

    &--error {
      color: #a53232;
    }
  }

  &__form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  &__form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;

    &--full {
      grid-column: 1 / -1;
    }

    label {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: .04em;
      color: var(--theme-text-tertiary);
    }

    input,
    textarea {
      border: 1px solid var(--theme-border-strong);
      border-radius: var(--theme-radius-sm, 3px);
      padding: 7px 9px;
      font-size: 12.5px;
      color: var(--theme-text-primary);
      background: var(--theme-bg-card);
    }

    textarea {
      resize: none;
      height: 42px;
    }
  }

  &__form-actions {
    grid-column: 1 / -1;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    margin-top: 2px;
  }

  &__form-error {
    font-size: 11.5px;
    color: #a53232;
  }

  &__btn-primary {
    background: var(--theme-accent-primary);
    color: #fff;
    border: none;
    border-radius: var(--theme-radius-sm, 3px);
    padding: 8px 14px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
      opacity: .6;
      cursor: default;
    }
  }

  // ── summary + quick alerts ─────────────────────────────────────────────
  &__summary {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 12px;
    margin-bottom: 12px;

    @media (max-width: 900px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  &__sum-card {
    position: relative;
    overflow: hidden;
    padding: 13px 16px;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: var(--theme-border-strong);
    }

    &--inuse::before { background: var(--theme-status-success); }
    &--inuse .domains-register__sval { color: #1c7a4d; }
    &--parked::before { background: var(--theme-status-info); }
    &--parked .domains-register__sval { color: #215d84; }
    &--abandoned::before { background: var(--theme-status-warning); }
    &--abandoned .domains-register__sval { color: #8a5a0d; }
    &--unknown::before { background: var(--theme-text-tertiary); }
    &--unknown .domains-register__sval { color: var(--theme-text-secondary); }
  }

  &__sval {
    font-size: 22px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
  }

  &__slabel {
    font-size: 11.5px;
    color: var(--theme-text-secondary);
    margin-top: 2px;
  }

  &__quick-alerts {
    display: flex;
    gap: 10px;
    margin-bottom: 14px;
    flex-wrap: wrap;
  }

  &__quick-alert {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    font-weight: 600;
    padding: 7px 13px;
    border-radius: 16px;
    cursor: pointer;
    user-select: none;

    &--warn {
      background: #fbeeda;
      color: #8a5a0d;
    }

    &--danger {
      background: #fbe1e1;
      color: #a53232;
    }

    &--on {
      outline: 2px solid currentColor;
      outline-offset: -2px;
    }
  }

  // ── rail + table ───────────────────────────────────────────────────────
  &__layout {
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 16px;
    align-items: start;

    @media (max-width: 1024px) {
      grid-template-columns: 1fr;
    }
  }

  &__rail {
    padding: 16px 16px 18px;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
  }

  &__rail-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    h4 {
      margin: 0;
      font-size: 13px;
      font-weight: 600;
    }
  }

  &__clear {
    font-size: 11px;
    color: var(--theme-accent-dark);
    font-weight: 600;
    cursor: pointer;
  }

  &__facet-group {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__facet-title {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .06em;
    color: var(--theme-text-tertiary);
    margin-bottom: 6px;
  }

  &__facet-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4.5px 2px;
    font-size: 12.5px;
    color: var(--theme-text-secondary);
    cursor: pointer;
    border-radius: 4px;

    &:hover {
      color: var(--theme-text-primary);
      background: var(--theme-bg-content);
    }

    &--active {
      color: var(--theme-text-primary);
      font-weight: 600;
    }
  }

  &__facet-box {
    width: 13px;
    height: 13px;
    border-radius: 3px;
    border: 1.5px solid var(--theme-border-strong);
    flex-shrink: 0;
    position: relative;

    .domains-register__facet-row--active & {
      background: var(--theme-accent-primary);
      border-color: var(--theme-accent-primary);

      &::after {
        content: "";
        position: absolute;
        left: 3px;
        top: 0;
        width: 4px;
        height: 7px;
        border: solid #fff;
        border-width: 0 1.6px 1.6px 0;
        transform: rotate(45deg);
      }
    }
  }

  &__facet-label {
    flex: 1;
  }

  &__facet-count {
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
  }

  &__grid {
    overflow: hidden;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);

    table {
      width: 100%;
      border-collapse: collapse;
    }

    thead th {
      text-align: left;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: .05em;
      text-transform: uppercase;
      color: var(--theme-text-tertiary);
      padding: 10px 14px;
      border-bottom: 1px solid var(--theme-border);
      background: var(--theme-bg-content);
      white-space: nowrap;
    }

    tbody td {
      padding: 11px 14px;
      font-size: 12.5px;
      border-bottom: 1px solid var(--theme-border-soft);
      vertical-align: top;
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    tbody tr:hover {
      background: var(--theme-bg-content);
      cursor: pointer;
    }
  }

  &__loading {
    text-align: center;
    color: var(--theme-text-tertiary);
    padding: 24px !important;
  }

  &__bulk-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 16px;
    background: var(--theme-accent-light);
    border-bottom: 1px solid var(--theme-border-soft);
    flex-wrap: wrap;
  }

  &__bcount {
    font-size: 12.5px;
    font-weight: 700;
    color: var(--theme-accent-dark);
  }

  &__bulk-btn {
    font-size: 11.5px;
    font-weight: 600;
    padding: 6px 11px;
    border-radius: var(--theme-radius-sm, 3px);
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);
    color: var(--theme-text-primary);
    cursor: pointer;

    &:disabled {
      opacity: .6;
      cursor: default;
    }

    &--ghost {
      border-color: transparent;
      background: transparent;
      color: var(--theme-accent-dark);
      margin-left: auto;
    }
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 11px 16px;
    border-bottom: 1px solid var(--theme-border-soft);
    flex-wrap: wrap;
  }

  &__search-box {
    display: flex;
    align-items: center;
    gap: 7px;
    border: 1px solid var(--theme-border-strong);
    border-radius: var(--theme-radius-sm, 3px);
    padding: 6px 10px;
    background: var(--theme-bg-content);
    min-width: 230px;

    svg {
      width: 13px;
      height: 13px;
      color: var(--theme-text-tertiary);
      flex-shrink: 0;
    }

    input {
      border: none;
      background: none;
      font-size: 12.5px;
      outline: none;
      color: var(--theme-text-primary);
      width: 100%;
    }
  }

  &__toolbar-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__sort-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--theme-text-secondary);
    border: 1px solid var(--theme-border-strong);
    border-radius: var(--theme-radius-sm, 3px);
    padding: 6px 10px;
    background: var(--theme-bg-card);
    cursor: pointer;
    white-space: nowrap;
  }
}
</style>
